/**
 * Schedule Meeting Page
 * =====================
 * Dedicated page for scheduling a meeting with a lead
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui';
import { DashboardLayout } from '../../components/layout';
import { MeetingScheduler } from '../../components/leads';
import { useToast } from '../../hooks';
import { leadAPI } from '../../api';

const ScheduleMeetingPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [leadName, setLeadName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        const response = await leadAPI.getLead(leadId);
        setLeadName(response.data.company || 'Lead');
      } catch (error) {
        console.error('Error fetching lead details:', error);
        showToast('Failed to load lead details', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchLeadDetails();
  }, [leadId, showToast]);

  const handleSuccess = (data) => {
    showToast(
      `Meeting scheduled successfully! Link: ${data.meeting_link || 'Check email'}`,
      'success'
    );
    navigate(`/leads/${leadId}`);
  };

  const handleCancel = () => {
    navigate(`/leads/${leadId}`);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
              Schedule Meeting
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Schedule a meeting with {leadName}
            </p>
          </div>
          <Button variant="outline" onClick={handleCancel}>
            ← Back to Lead Details
          </Button>
        </div>

        {/* Meeting Scheduler */}
        <MeetingScheduler
          leadId={leadId}
          leadName={leadName}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </div>
    </DashboardLayout>
  );
};

export default ScheduleMeetingPage;

